<script setup>
import { ref, reactive, watch } from "vue";
import {
  queryPurificationStandardByType,
  adjustPurificationStandard,
} from "@/api/effluent";
import { ElMessage } from "element-plus";

const props = defineProps({
  pageParams: {
    type: Object,
  },
});

const emits = defineEmits(["afterSubmit"]);

// 表单ref
const ruleRef = ref(null);

// 页面loading
const loading = ref(false);

// 表单数据
const formData = ref({
  type: null,
  cod: null,
  bod: null,
  ammoniaNitrogenHigh: null,
  ammoniaNitrogenLow: null,
  ammoniaNitrogen: null,
  totalNitrogen: null,
  totalPhosphorus: null,
  ss: null,
  chroma: null,
  phStart: null,
  phEnd: null,
  fecalColiform: null,
  vegetableOil: null,
  oil: null,
  anionicSurfactant: null,
});

// 自定义表单校验规则
const floatReg = /^(([1-9]{1}[0-9]{0,7})|(0{1}))(\.\d{1,2})?$/;
const validatePosNumber = (rule, value, callback) => {
  if (rule.field != "nitrogen" || rule.field != "ph") {
    if (floatReg.test(value)) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数且只能保留两位小数"));
    }
  }
};

const validateMergeNum = (rule, value, callback) => {
  if (rule.field == "nitrogen") {
    const num1 = formData.value.ammoniaNitrogenHigh;
    const num2 = formData.value.ammoniaNitrogenLow;
    if (floatReg.test(num1) && floatReg.test(num2)) {
      callback();
    } else if (num1 === "" || num1 === null || num2 === "" || num2 === null) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数且只能保留两位小数"));
    }
  } else if (rule.field == "ph") {
    const num3 = formData.value.phStart;
    const num4 = formData.value.phEnd;
    if (Number(num3) <= Number(num4)) {
      callback("上限不能小于等于下限!");
    } else if (floatReg.test(num3) && floatReg.test(num4)) {
      callback();
    } else if (num3 === "" || num3 === null || num4 === "" || num4 === null) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数且只能保留两位小数"));
    }
  }
};

const rules = reactive({
  cod: [{ validator: validatePosNumber, trigger: "blur" }],
  bod: [{ validator: validatePosNumber, trigger: "blur" }],
  ss: [{ validator: validatePosNumber, trigger: "blur" }],
  vegetableOil: [{ validator: validatePosNumber, trigger: "blur" }],
  oil: [{ validator: validatePosNumber, trigger: "blur" }],
  anionicSurfactant: [{ validator: validatePosNumber, trigger: "blur" }],
  totalNitrogen: [{ validator: validatePosNumber, trigger: "blur" }],
  nitrogen: [{ validator: validateMergeNum, trigger: "blur" }],
  totalPhosphorus: [{ validator: validatePosNumber, trigger: "blur" }],
  chroma: [{ validator: validatePosNumber, trigger: "blur" }],
  ph: [{ validator: validateMergeNum, trigger: "blur" }],
  fecalColiform: [{ validator: validatePosNumber, trigger: "blur" }],
  ammoniaNitrogen: [{ validator: validateMergeNum, trigger: "blur" }],
});

// 获取标准详情
const getData = () => {
  loading.value = true;
  queryPurificationStandardByType(formData.value.type)
    .then((res) => {
      if (JSON.stringify(res.data) === "{}") {
        formData.value = {
          type: props.pageParams.type,
          cod: null,
          bod: null,
          ammoniaNitrogenHigh: null,
          ammoniaNitrogenLow: null,
          ammoniaNitrogen: null,
          totalNitrogen: null,
          totalPhosphorus: null,
          ss: null,
          chroma: null,
          phStart: null,
          phEnd: null,
          fecalColiform: null,
          vegetableOil: null,
          oil: null,
          anionicSurfactant: null,
        };
      } else {
        formData.value = res.data;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

// 保存
const submitForm = () => {
  ruleRef.value?.validate((valid) => {
    if (valid) {
      //调用新增或者修改接口
      adjustPurificationStandard(formData.value).then((res) => {
        if (res.code == "2000") {
          ElMessage.success("操作成功");
          emits("afterSubmit");
        }
      });
    }
  });
};

watch(
  () => props.pageParams,
  (newVal) => {
    if (newVal) {
      formData.value.type = newVal.type;
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

defineExpose({
  submitForm,
  getData,
});
</script>

<template>
  <el-form
    :model="formData"
    :rules="rules"
    ref="ruleRef"
    label-position="top"
    v-loading="loading"
  >
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="化学需氧量(CODcr)" prop="cod">
          <el-input v-model="formData.cod" placeholder="请输入">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="生化需氧量(BOD5)" prop="bod">
          <el-input v-model="formData.bod" placeholder="请输入">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item
          label="氨氮"
          prop="nitrogen"
          v-if="![2, 3].includes(props.pageParams.type)"
        >
          <el-row :gutter="10">
            <el-col :span="12">
              <el-input
                placeholder="请输入"
                v-model="formData.ammoniaNitrogenHigh"
              >
                <template #prepend>大于12℃</template>
              </el-input>
            </el-col>
            <el-col :span="12">
              <el-input
                placeholder="请输入"
                v-model="formData.ammoniaNitrogenLow"
              >
                <template #prepend>小于等于12℃</template>
              </el-input>
            </el-col>
          </el-row>
        </el-form-item>
        <el-form-item label="氨氮" prop="ammoniaNitrogen" v-else>
          <el-input placeholder="请输入" v-model="formData.ammoniaNitrogen">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="总氮(TN)" prop="totalNitrogen">
          <el-input placeholder="请输入" v-model="formData.totalNitrogen">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="总磷(TP)" prop="totalPhosphorus">
          <el-input placeholder="请输入" v-model="formData.totalPhosphorus">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="悬浮物(SS)" prop="ss">
          <el-input placeholder="请输入" v-model="formData.ss">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="色度(稀释倍数)" prop="chroma">
          <el-input placeholder="请输入" v-model="formData.chroma">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="pH" prop="ph">
          <el-row :gutter="10">
            <el-col :span="12">
              <el-input placeholder="请输入" v-model="formData.phStart">
                <template #prepend>上限:</template>
              </el-input>
            </el-col>
            <el-col :span="12">
              <el-input placeholder="请输入" v-model="formData.phEnd">
                <template #prepend>下限:</template>
              </el-input>
            </el-col>
          </el-row>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="粪大肠菌群数" prop="fecalColiform">
          <el-input placeholder="请输入" v-model="formData.fecalColiform">
            <template #append>个/L</template>
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="动植物油" prop="vegetableOil">
          <el-input placeholder="请输入" v-model="formData.vegetableOil">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="石油类" prop="oil">
          <el-input placeholder="请输入" v-model="formData.oil">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="阴离子表面活性剂" prop="anionicSurfactant">
          <el-input placeholder="请输入" v-model="formData.anionicSurfactant">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<style lang="scss" scoped></style>
